import React, { useState } from 'react';
import { Box, Button, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Home, Download, Delete } from '@mui/icons-material';
import { useDarkMode } from '../context/DarkModeContext';
import TextInputArea from '../components/TextInputArea';
import JsonCodeViewer from '../components/JsonCodeViewer';
import Footer from '../components/Footer';

const JsonFormatterPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [error, setError] = useState('');
  const [csvResult, setCsvResult] = useState('');
  const [csvModalOpen, setCsvModalOpen] = useState(false);

  const handleFormat = () => {
    try {
      if (!inputText.trim()) {
        setError('Please enter JSON to format');
        return;
      }
      const parsed = JSON.parse(inputText);
      const formatted = JSON.stringify(parsed, null, 2);
      setOutputText(formatted);
      setError('');
    } catch (err) {
      setError('Invalid JSON format');
      setOutputText('');
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleDownload = () => {
    const blob = new Blob([outputText], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'formatted.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleClear = () => {
    setInputText('');
    setOutputText('');
    setError('');
  };

  function findFirstArray(obj) {
    if (Array.isArray(obj)) return obj;
    if (typeof obj === 'object' && obj !== null) {
      for (const key in obj) {
        const found = findFirstArray(obj[key]);
        if (found) return found;
      }
    }
    return null;
  }

  function jsonToCsv(json) {
    const arr = findFirstArray(json);
    if (!arr || !Array.isArray(arr) || arr.length === 0) return '';
    const keys = Array.from(new Set(arr.flatMap(obj => Object.keys(obj))));
    const escape = (val) =>
      typeof val === 'string' && (val.includes(',') || val.includes('"') || val.includes('\n'))
        ? '"' + val.replace(/"/g, '""') + '"'
        : val;
    const header = keys.join(',');
    const rows = arr.map(obj => keys.map(k => escape(obj[k] ?? '')).join(','));
    return [header, ...rows].join('\n');
  }

  const handleConvertToCsv = () => {
    try {
      const parsed = JSON.parse(outputText);
      const csv = jsonToCsv(parsed);
      if (!csv) {
        setError('No array of objects found to convert to CSV.');
        return;
      }
      setCsvResult(csv);
      setCsvModalOpen(true);
      setError('');
    } catch {
      setError('Invalid JSON for CSV conversion.');
    }
  };

  const handleCopyCsv = () => {
    navigator.clipboard.writeText(csvResult);
  };

  const handleDownloadCsv = () => {
    const blob = new Blob([csvResult], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'converted.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleGenerateSampleJson = () => {
    const sample = {
      "people": [
        {
          "name": "Alice",
          "age": 30,
          "isDeveloper": true,
          "address": {
            "street": "123 Code Lane",
            "city": "Boston"
          }
        },
        {
          "name": "John",
          "age": 28,
          "isDeveloper": false,
          "address": {
            "street": "456 Bug Street",
            "city": "New York"
          }
        }
      ]
    };
    setInputText(JSON.stringify(sample, null, 2));
    setOutputText('');
    setError('');
  };

  const actionButtonSx = {
    color: '#fff',
    textDecoration: 'none',
    fontSize: '1rem',
    padding: '8px 16px',
    borderRadius: '4px',
    bgcolor: '#388e3c',
    textTransform: 'none',
    minWidth: 140,
    '&:hover': {
      bgcolor: '#2e7d32'
    },
    '&.Mui-disabled': {
      bgcolor: '#a5d6a7',
      color: '#fff'
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: theme.palette.background.default,
      color: theme.palette.text.primary,
      p: 3,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Dark mode toggle in top right corner */}
      <button
        onClick={toggleDarkMode}
        className="dark-mode-toggle"
        style={{ 
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 1000,
          color: '#fff',
          fontSize: '1rem',
          padding: '8px 16px',
          borderRadius: '4px',
          backgroundColor: '#388e3c',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '40px'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#2e7d32'}
        onMouseLeave={(e) => e.target.style.backgroundColor = '#388e3c'}
      >
        <span className="dark-mode-text">
          {darkMode ? '☀️ Light Mode' : '🌙 Dark Mode'}
        </span>
        <span className="dark-mode-icon">
          {darkMode ? '☀️' : '🌙'}
        </span>
      </button>

      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        mb: 3,
        gap: { xs: 2, sm: 0 }
      }}>
        <Button
          startIcon={<Home />}
          onClick={() => navigate('/')}
          sx={{ 
            color: '#fff',
            textDecoration: 'none',
            fontSize: '1rem',
            padding: '8px 16px',
            borderRadius: '4px',
            bgcolor: '#388e3c',
            textTransform: 'none',
            width: { xs: '100%', sm: 'auto' },
            '&:hover': {
              bgcolor: '#2e7d32'
            }
          }}
        >
          Home
        </Button>
        <Typography variant="h4" component="h1" sx={{ 
          flexGrow: 1, 
          textAlign: 'center',
          color: '#388e3c',
          fontWeight: 'bold',
          letterSpacing: '1px',
          fontSize: { xs: '2rem', sm: '2.5rem' }
        }}>
          JSON Validator
        </Typography>
      </Box>

      {/* Main Content */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        height: { xs: 'auto', md: 'calc(100vh - 200px)' },
        overflow: 'hidden'
      }}>
        {/* Input Section */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          minHeight: 0,
          mb: { xs: 2, md: 0 }
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ color: darkMode ? '#4CAF50' : '#006400', fontWeight: 'bold' }}>Input JSON</Typography>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <TextInputArea
              value={inputText}
              onChange={(value) => setInputText(value)}
              placeholder="Enter your JSON here..."
              onCopy={() => handleCopy(inputText)}
              sx={{ height: '100%' }}
            />
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'row', sm: 'row', md: 'column' },
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          mb: { xs: 2, md: 0 }
        }}>
          <Button
            variant="contained"
            onClick={handleFormat}
            disabled={!inputText}
            sx={{ ...actionButtonSx, flexBasis: { xs: '48%', md: 'auto' }, minWidth: { xs: 120, md: 140 } }}
          >
            Format
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerateSampleJson}
            sx={{ ...actionButtonSx, flexBasis: { xs: '48%', md: 'auto' }, minWidth: { xs: 120, md: 140 } }}
          >
            Sample JSON
          </Button>
          <Button
            variant="contained"
            onClick={handleConvertToCsv}
            disabled={!outputText}
            sx={{ ...actionButtonSx, flexBasis: { xs: '48%', md: 'auto' }, minWidth: { xs: 120, md: 140 } }}
          >
            Convert to CSV
          </Button>
          <Button
            variant="outlined"
            onClick={handleClear}
            disabled={!inputText && !outputText}
            startIcon={<Delete />}
            sx={{
              color: '#388e3c',
              borderColor: '#388e3c',
              background: 'transparent',
              textTransform: 'none',
              flexBasis: { xs: '48%', md: 'auto' },
              minWidth: { xs: 120, md: 140 },
              fontSize: '1rem',
              '&:hover': {
                background: 'rgba(56, 142, 60, 0.08)',
                borderColor: '#2e7d32',
                color: '#2e7d32'
              },
              '&.Mui-disabled': {
                color: '#a5d6a7',
                borderColor: '#a5d6a7',
                background: 'transparent'
              }
            }}
          >
            Clear
          </Button>
        </Box>

        {/* Output Section */}
        <Box className="output-section" sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          minHeight: 0
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ color: darkMode ? '#4CAF50' : '#006400', fontWeight: 'bold' }}>Output JSON</Typography>
            <Button
              startIcon={<Download />}
              onClick={handleDownload}
              disabled={!outputText}
              size="small"
              sx={{
                color: '#fff',
                bgcolor: '#388e3c',
                textTransform: 'none',
                padding: '6px 16px',
                minWidth: 100,
                '&:hover': {
                  bgcolor: '#2e7d32'
                }
              }}
            >
              Download
            </Button>
          </Box>
          <Box className="output-section" sx={{ flex: 1, minHeight: 0, height: '100%', overflow: 'auto', borderRadius: 2, p: 0, display: 'flex', flexDirection: 'column' }}>
            {outputText ? (
              (() => {
                try {
                  const parsed = JSON.parse(outputText);
                  return <JsonCodeViewer json={parsed} style={{ height: '100%', width: '100%' }} />;
                } catch {
                  return <TextInputArea
                    value={outputText}
                    onChange={() => {}}
                    readOnly
                    placeholder="Formatted JSON will appear here..."
                    onCopy={() => handleCopy(outputText)}
                    sx={{ height: '100%' }}
                  />;
                }
              })()
            ) : (
              <TextInputArea
                value={outputText}
                onChange={() => {}}
                readOnly
                placeholder="Formatted JSON will appear here..."
                onCopy={() => handleCopy(outputText)}
                sx={{ height: '100%' }}
              />
            )}
          </Box>
        </Box>

        {error && (
          <Typography color="error" sx={{ 
            position: 'absolute',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)'
          }}>
            {error}
          </Typography>
        )}
      </Box>

      {/* CSV Modal */}
      <Dialog open={csvModalOpen} onClose={() => setCsvModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>CSV Result</DialogTitle>
        <DialogContent>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 14 }}>{csvResult}</pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopyCsv}>Copy</Button>
          <Button onClick={handleDownloadCsv}>Download</Button>
          <Button onClick={() => setCsvModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Footer />
    </Box>
  );
};

export default JsonFormatterPage; 